import type { IGitService, RepoReadinessReport, RepoInitOutcome } from './IGitService.interface'
import type {
  Branch,
  BranchFileChange,
  CICheck,
  CIStatus,
  Commit,
  CommitDetail,
  CommitFilter,
  Comparison,
  CreatePRInput,
  CreateReleaseInput,
  FileDiff,
  PR,
  PRDetail,
  PRFilter,
  Release,
  Repo,
  RepoRef,
  Review,
  RunFilter,
  TreeEntry,
  WorkflowRun,
  PrApprovalState,
} from '../../types/git.types'
import type {
  MergeConflictReport,
  LandReviewReport,
  ResolveMergeConflictsInput,
  ResolveMergeConflictsResult,
} from '@stride/shared'
import { deriveBranchType } from './branchType'

// ── Static seed data ──────────────────────────────────────────────────────────

const MOCK_REPOS: Repo[] = [
  {
    id:            'repo-arm',
    name:          'Agentforce Revenue Management',
    owner:         'stratuscarta',
    fullName:      'stratuscarta/arm',
    description:   'ARM Salesforce Revenue Cloud deployment automation',
    defaultBranch: 'main',
    visibility:    'private',
    lastPushedAt:  new Date(Date.now() - 3_600_000).toISOString(),
    openPRCount:   2,
    url:           'https://dev.azure.com/stratuscarta/arm',
  },
  {
    id:            'repo-stride',
    name:          'Stride',
    owner:         'stratuscarta',
    fullName:      'stratuscarta/stride',
    description:   'Internal Salesforce DevOps platform',
    defaultBranch: 'main',
    visibility:    'private',
    lastPushedAt:  new Date(Date.now() - 7_200_000).toISOString(),
    openPRCount:   1,
    url:           'https://dev.azure.com/stratuscarta/stride',
  },
]

const BASE_COMMIT: Commit = {
  sha: 'abc1234567890abcdef1234567890abcdef12345',
  shortSha: 'abc1234',
  message: 'feat: initial ARM deployment layer structure\n\nAdds layer 01–09 with retry logic.',
  summary: 'feat: initial ARM deployment layer structure',
  author: {
    name: 'Anuj Vaishampayan',
    email: 'anuj@stratuscarta.com',
    avatarUrl: null,
  },
  date: new Date(Date.now() - 86_400_000).toISOString(),
  changedFilesCount: 12,
}

const MOCK_COMMITS: Commit[] = [
  BASE_COMMIT,
  {
    sha: 'def5678901234def5678901234def567890123456',
    shortSha: 'def5678',
    message: 'fix: retry logic for Layer 03 Product2 type errors',
    summary: 'fix: retry logic for Layer 03 Product2 type errors',
    author: { name: 'Anuj Vaishampayan', email: 'anuj@stratuscarta.com', avatarUrl: null },
    date: new Date(Date.now() - 43_200_000).toISOString(),
    changedFilesCount: 3,
  },
  {
    sha: 'ghi9012345678ghi9012345678ghi9012345678901',
    shortSha: 'ghi9012',
    message: 'chore: update pnpm lockfile',
    summary: 'chore: update pnpm lockfile',
    author: { name: 'Anuj Vaishampayan', email: 'anuj@stratuscarta.com', avatarUrl: null },
    date: new Date(Date.now() - 3_600_000).toISOString(),
    changedFilesCount: 1,
  },
  {
    sha: 'jkl3456789012jkl3456789012jkl34567890123456',
    shortSha: 'jkl3456',
    message: 'docs: add ARM deployment guide\n\nDocuments layer sequencing and retry patterns.',
    summary: 'docs: add ARM deployment guide',
    author: { name: 'Anuj Vaishampayan', email: 'anuj@stratuscarta.com', avatarUrl: null },
    date: new Date(Date.now() - 172_800_000).toISOString(),
    changedFilesCount: 2,
  },
  {
    sha: 'mno7890123456mno7890123456mno789012345678901',
    shortSha: 'mno7890',
    message: 'refactor: extract layer base utilities\n\nMoves shared helpers to layer-utils.ts.',
    summary: 'refactor: extract layer base utilities',
    author: { name: 'Anuj Vaishampayan', email: 'anuj@stratuscarta.com', avatarUrl: null },
    date: new Date(Date.now() - 259_200_000).toISOString(),
    changedFilesCount: 4,
  },
]

const MOCK_BRANCHES_RAW: Array<Omit<Branch, 'type'>> = [
  {
    name: 'main',
    lastCommit: BASE_COMMIT,
    aheadBy: 0,
    behindBy: 0,
    isProtected: true,
    hasOpenPR: false,
  },
  {
    name: 'phase/2-arm-engine',
    lastCommit: MOCK_COMMITS[1] ?? BASE_COMMIT,
    aheadBy: 5,
    behindBy: 1,
    isProtected: false,
    hasOpenPR: true,
  },
  {
    name: 'feature/git-service',
    lastCommit: MOCK_COMMITS[2] ?? BASE_COMMIT,
    aheadBy: 8,
    behindBy: 0,
    isProtected: false,
    hasOpenPR: false,
  },
  {
    name: 'fix/layer03-retry',
    lastCommit: MOCK_COMMITS[1] ?? BASE_COMMIT,
    aheadBy: 2,
    behindBy: 3,
    isProtected: false,
    hasOpenPR: true,
  },
  {
    name: 'other/experimental',
    lastCommit: BASE_COMMIT,
    aheadBy: 1,
    behindBy: 0,
    isProtected: false,
    hasOpenPR: false,
  },
]

const MOCK_BRANCHES: Branch[] = MOCK_BRANCHES_RAW.map((b) => ({
  ...b,
  type: deriveBranchType(b.name),
}))

const MOCK_PRS: PR[] = [
  {
    number: 1,
    title: 'Phase 2: ARM Deployment Engine',
    state: 'open',
    author: { name: 'Anuj Vaishampayan', avatarUrl: null },
    head: 'phase/2-arm-engine',
    base: 'main',
    createdAt: new Date(Date.now() - 172_800_000).toISOString(),
    updatedAt: new Date(Date.now() - 3_600_000).toISOString(),
    labels: ['enhancement', 'phase-2'],
    reviewStatus: 'pending',
    approvalCount: 0,
    ciStatus: 'passing',
    mergeStatus: 'clean',
    url: 'https://dev.azure.com/stratuscarta/arm/pullrequest/1',
  },
  {
    number: 2,
    title: 'Fix: Layer 03 retry logic for Product2 type errors',
    state: 'open',
    author: { name: 'Anuj Vaishampayan', avatarUrl: null },
    head: 'fix/layer03-retry',
    base: 'main',
    createdAt: new Date(Date.now() - 86_400_000).toISOString(),
    updatedAt: new Date(Date.now() - 7_200_000).toISOString(),
    labels: ['bug'],
    reviewStatus: 'approved',
    approvalCount: 1,
    ciStatus: 'passing',
    mergeStatus: 'clean',
    url: 'https://dev.azure.com/stratuscarta/arm/pullrequest/2',
  },
  {
    number: 3,
    title: 'WIP: Add billing integration',
    state: 'open',
    author: { name: 'Anuj Vaishampayan', avatarUrl: null },
    head: 'feature/billing',
    base: 'main',
    createdAt: new Date(Date.now() - 259_200_000).toISOString(),
    updatedAt: new Date(Date.now() - 86_400_000).toISOString(),
    labels: ['wip'],
    reviewStatus: 'changes_requested',
    approvalCount: 0,
    ciStatus: 'failing',
    mergeStatus: 'conflicts',
    url: 'https://dev.azure.com/stratuscarta/arm/pullrequest/3',
  },
]

const MOCK_RELEASES: Release[] = [
  {
    id: 'rel-v2.4.1',
    tagName: 'v2.4.1',
    name: 'Release v2.4.1',
    body: '## What\'s changed\n\n- Layer 9 pricing migration\n- BasedOnId wiring fix',
    isDraft: false,
    isPrerelease: false,
    createdAt:   new Date(Date.now() -  8 * 86_400_000).toISOString(),
    publishedAt: new Date(Date.now() -  8 * 86_400_000).toISOString(),
    author: { name: 'Anuj Vaishampayan', avatarUrl: null },
    commitsSinceLastRelease: 14,
  },
  {
    id: 'rel-v2.3.0',
    tagName: 'v2.3.0',
    name: 'Release v2.3.0',
    body: '## What\'s changed\n\n- Bundle structure support added for nested product configurations\n- Catalog migration with full GlobalKey__c wiring and retry logic for transient failures',
    isDraft: false,
    isPrerelease: false,
    createdAt:   new Date(Date.now() - 30 * 86_400_000).toISOString(),
    publishedAt: new Date(Date.now() - 30 * 86_400_000).toISOString(),
    author: { name: 'Anuj Vaishampayan', avatarUrl: null },
    commitsSinceLastRelease: 68,
  },
  {
    id: 'rel-v2.5.0-beta',
    tagName: 'v2.5.0-beta',
    name: 'Beta v2.5.0',
    body: 'Pre-release of pipeline canvas feature',
    isDraft: false,
    isPrerelease: true,
    createdAt:   new Date(Date.now() -  2 * 86_400_000).toISOString(),
    publishedAt: new Date(Date.now() -  2 * 86_400_000).toISOString(),
    author: { name: 'Anuj Vaishampayan', avatarUrl: null },
    commitsSinceLastRelease: 5,
  },
]

const MOCK_WORKFLOW_RUNS: WorkflowRun[] = [
  {
    id: 'run-001',
    name: 'CI',
    branch: 'main',
    status: 'completed',
    conclusion: 'success',
    startedAt: new Date(Date.now() - 3_600_000).toISOString(),
    completedAt: new Date(Date.now() - 3_400_000).toISOString(),
    url: 'https://dev.azure.com/stratuscarta/arm/_build/results?buildId=1',
  },
  {
    id: 'run-002',
    name: 'CI',
    branch: 'phase/2-arm-engine',
    status: 'completed',
    conclusion: 'success',
    startedAt: new Date(Date.now() - 7_200_000).toISOString(),
    completedAt: new Date(Date.now() - 7_000_000).toISOString(),
    url: 'https://dev.azure.com/stratuscarta/arm/_build/results?buildId=2',
  },
]

// ── Shared comparison files (reused by compareRefs + getPR for PR #1) ────────

const MOCK_COMPARISON_FILES: FileDiff[] = [
  {
    filename: 'src/jobs/arm/migrate_layer9.py',
    status: 'modified',
    additions: 24,
    deletions: 8,
    hunks: [
      {
        header: '@@ -10,8 +10,24 @@',
        lines: [
          { type: 'context',  content: 'def run_layer9():',            oldLineNumber: 10,   newLineNumber: 10   },
          { type: 'deletion', content: '    batch_size = 50',          oldLineNumber: 11,   newLineNumber: null },
          { type: 'addition', content: '    batch_size = 200',         oldLineNumber: null, newLineNumber: 11   },
          { type: 'context',  content: '    records = query_source()', oldLineNumber: 12,   newLineNumber: 12   },
        ],
      },
    ],
    previousFilename: null,
  },
  {
    filename: 'src/layers/layer03.ts',
    status: 'modified',
    additions: 14,
    deletions: 5,
    hunks: [
      {
        header: '@@ -1,5 +1,14 @@',
        lines: [
          { type: 'context',  content: "import { Layer } from '../base'",      oldLineNumber: 1,    newLineNumber: 1 },
          { type: 'addition', content: "import { retry } from '../retry'",     oldLineNumber: null, newLineNumber: 2 },
          { type: 'context',  content: 'export class Layer03 extends Layer {', oldLineNumber: 2,    newLineNumber: 3 },
        ],
      },
    ],
    previousFilename: null,
  },
  {
    filename: 'jobs/arm/layer09.json',
    status: 'added',
    additions: 42,
    deletions: 0,
    hunks: [
      {
        header: '@@ -0,0 +1,42 @@',
        lines: [
          { type: 'addition', content: '{',                               oldLineNumber: null, newLineNumber: 1 },
          { type: 'addition', content: '  "name": "Layer 09 — Pricing"', oldLineNumber: null, newLineNumber: 2 },
          { type: 'addition', content: '  "batchSize": 200',              oldLineNumber: null, newLineNumber: 3 },
          { type: 'addition', content: '}',                               oldLineNumber: null, newLineNumber: 4 },
        ],
      },
    ],
    previousFilename: null,
  },
]

const MOCK_REVIEWS_PR1: Review[] = [
  {
    id: 'rev-1',
    author: { name: 'Sarah Chen', avatarUrl: null },
    state: 'approved',
    body: 'LGTM — changes look good.',
    submittedAt: new Date(Date.now() - 3_600_000).toISOString(),
  },
  {
    id: 'rev-2',
    author: { name: 'Dev User', avatarUrl: null },
    state: 'commented',
    body: 'Minor nit: consider extracting to a utility function.',
    submittedAt: new Date(Date.now() - 7_200_000).toISOString(),
  },
]

// ── Mock branch filesystem ──────────────────────────────────────────────────────
// A representative committed tree (metadata + ARM/CML config-as-data + the set manifest),
// varied per branch so the file browser shows real folder structure and the cross-branch
// compare surfaces genuine added/removed/modified files.

const MOCK_REPO_BASE: Record<string, string> = {
  'package.json': '{\n  "name": "armtest",\n  "version": "1.0.0",\n  "private": true\n}\n',
  '.stride/set.json': '{\n  "slug": "residential-internet",\n  "name": "Residential Internet",\n  "lanes": { "arm": {}, "cml": {} }\n}\n',
  'force-app/main/default/classes/RatingService.cls':
    'public with sharing class RatingService {\n  public Decimal rate(Decimal qty) {\n    return qty * 9.99;\n  }\n}\n',
  'force-app/main/default/classes/RatingService.cls-meta.xml':
    '<?xml version="1.0" encoding="UTF-8"?>\n<ApexClass xmlns="http://soap.sforce.com/2006/04/metadata">\n  <apiVersion>62.0</apiVersion>\n  <status>Active</status>\n</ApexClass>\n',
  'data/arm/cml/Residential_Internet/Residential_Internet_V1.cml':
    'type residentialinternet : LineItem {\n  relation modem : LineItem;\n  constraint modem.qty == 1;\n}\n',
  'data/arm/cml/Residential_Internet/Residential_Internet_V1.json':
    '{\n  "developerName": "Residential_Internet_V1",\n  "esdDeveloperName": "Residential_Internet"\n}\n',
  'data/arm/manifests/residential-internet.gk-prod-1.json':
    '{\n  "component": "residential-internet",\n  "componentObject": "Product2",\n  "componentGlobalKey": "gk-prod-1",\n  "nodes": [\n    { "object": "Product2", "globalKey": "gk-prod-1", "label": "Residential Internet" }\n  ]\n}\n',
  'data/arm/PCM/Product2/ab/gk-prod-1.json':
    '{\n  "object": "Product2",\n  "globalKey": "gk-prod-1",\n  "record": {\n    "Name": "Residential Internet",\n    "ProductCode": "PRD-RES-INTERNET"\n  }\n}\n',
}

/** The committed files on a branch — `main` is the base; feature/fix branches carry edits + a new file. */
function mockBranchFiles(branch: string): Record<string, string> {
  const files = { ...MOCK_REPO_BASE }
  // main / org long-lived branches mirror the base; working branches diverge so a compare has content.
  if (branch === 'main' || branch.startsWith('org/')) return files
  files['force-app/main/default/classes/RatingService.cls'] =
    'public with sharing class RatingService {\n  public Decimal rate(Decimal qty) {\n    return qty * 10.99;  // repriced\n  }\n}\n'
  files['data/arm/cml/Residential_Internet/Residential_Internet_V1.cml'] =
    'type residentialinternet : LineItem {\n  relation modem : LineItem;\n  constraint modem.qty >= 1;\n}\n'
  files['force-app/main/default/classes/PromoEngine.cls'] =
    `public with sharing class PromoEngine {\n  // added on ${branch}\n  public Decimal discount() { return 0.10; }\n}\n`
  return files
}

/** Expand a flat path list into the GitHub-style recursive tree (blobs + their ancestor dirs), sorted. */
function treeFromPaths(paths: string[]): TreeEntry[] {
  const seen = new Map<string, TreeEntry>()
  for (const path of paths) {
    seen.set(path, { path, type: 'blob' })
    const parts = path.split('/')
    for (let i = 1; i < parts.length; i++) {
      const dir = parts.slice(0, i).join('/')
      if (!seen.has(dir)) seen.set(dir, { path: dir, type: 'tree' })
    }
  }
  return [...seen.values()].sort((a, b) => a.path.localeCompare(b.path))
}

// ── MockGitService ────────────────────────────────────────────────────────────

export class MockGitService implements IGitService {
  private branches  = new Map<string, Branch[]>()
  private prs       = new Map<string, PR[]>()
  private _releases = new Map<string, Release[]>()
  // Operator-edited PR bodies keyed "owner/repo#number" — overrides the seeded body.
  private _prBodies = new Map<string, string>()

  private branchKey(ref: RepoRef): string {
    return `${ref.owner}/${ref.repo}`
  }

  private getBranches(ref: RepoRef): Branch[] {
    const key = this.branchKey(ref)
    if (!this.branches.has(key)) {
      this.branches.set(key, [...MOCK_BRANCHES])
    }
    return this.branches.get(key) ?? []
  }

  private getPRs(ref: RepoRef): PR[] {
    const key = this.branchKey(ref)
    if (!this.prs.has(key)) {
      this.prs.set(key, [...MOCK_PRS])
    }
    return this.prs.get(key) ?? []
  }

  private getReleases(ref: RepoRef): Release[] {
    const key = this.branchKey(ref)
    if (!this._releases.has(key)) {
      this._releases.set(key, [...MOCK_RELEASES])
    }
    // Return sorted by publishedAt / createdAt descending (newest first)
    return [...(this._releases.get(key) ?? [])].sort((a, b) => {
      const aDate = a.publishedAt ?? a.createdAt
      const bDate = b.publishedAt ?? b.createdAt
      return new Date(bDate).getTime() - new Date(aDate).getTime()
    })
  }

  // ── Repos ──────────────────────────────────────────────────────────────────

  /** The mock repo is already set up — a mock that reported "empty" would offer to initialize nothing. */
  async getReadiness(ref: RepoRef): Promise<RepoReadinessReport> {
    return {
      owner: ref.owner, repo: ref.repo, defaultBranch: 'main',
      readiness: 'ready', missing: [], consequences: [], manualSteps: '',
    }
  }

  async initializeRepo(ref: RepoRef): Promise<RepoInitOutcome> {
    // Never fabricates a commit: mock mode must not imply a repository was changed.
    return { owner: ref.owner, repo: ref.repo, branch: 'main', created: [], skipped: [] }
  }

  listRepos(): Promise<Repo[]> {
    return Promise.resolve([...MOCK_REPOS])
  }

  getRepo(ref: RepoRef): Promise<Repo> {
    const repo = MOCK_REPOS.find((r) => r.fullName === `${ref.owner}/${ref.repo}`)
    if (!repo) return Promise.reject(new Error(`Repo '${ref.owner}/${ref.repo}' not found`))
    return Promise.resolve({ ...repo })
  }

  // ── Branches ──────────────────────────────────────────────────────────────

  listBranches(ref: RepoRef): Promise<Branch[]> {
    return Promise.resolve(this.getBranches(ref).map((b) => ({ ...b })))
  }

  getBranch(ref: RepoRef, branch: string): Promise<Branch> {
    const b = this.getBranches(ref).find((br) => br.name === branch)
    if (!b) return Promise.reject(new Error(`Branch '${branch}' not found`))
    return Promise.resolve({ ...b })
  }

  createBranch(ref: RepoRef, name: string, _fromBranch: string): Promise<Branch> {
    const existing = this.getBranches(ref).find((b) => b.name === name)
    if (existing) return Promise.reject(new Error(`Branch '${name}' already exists`))

    const newBranch: Branch = {
      name,
      lastCommit: { ...BASE_COMMIT },
      aheadBy: 0,
      behindBy: 0,
      isProtected: false,
      hasOpenPR: false,
      type: deriveBranchType(name),
    }
    const key = this.branchKey(ref)
    const branches = this.getBranches(ref)
    this.branches.set(key, [...branches, newBranch])
    return Promise.resolve({ ...newBranch })
  }

  deleteBranch(ref: RepoRef, branch: string): Promise<void> {
    const branches = this.getBranches(ref)
    const idx = branches.findIndex((b) => b.name === branch)
    if (idx === -1) return Promise.reject(new Error(`Branch '${branch}' not found`))
    const key = this.branchKey(ref)
    this.branches.set(key, branches.filter((_, i) => i !== idx))
    return Promise.resolve()
  }

  // ── Commits ────────────────────────────────────────────────────────────────

  listCommits(_ref: RepoRef, filter?: CommitFilter): Promise<Commit[]> {
    let result = [...MOCK_COMMITS]
    if (filter?.author) {
      const a = filter.author.toLowerCase()
      result = result.filter(
        (c) =>
          c.author.name.toLowerCase().includes(a) ||
          c.author.email.toLowerCase().includes(a),
      )
    }
    if (filter?.limit !== undefined) result = result.slice(0, filter.limit)
    return Promise.resolve(result)
  }

  getCommit(_ref: RepoRef, sha: string): Promise<CommitDetail> {
    const commit = MOCK_COMMITS.find((c) => c.sha.startsWith(sha) || c.shortSha === sha)
    if (!commit) return Promise.reject(new Error(`Commit '${sha}' not found`))
    const detail: CommitDetail = {
      ...commit,
      files: [
        {
          filename: 'src/layers/layer03.ts',
          status: 'modified',
          additions: 24,
          deletions: 8,
          patch: '@@ -10,8 +10,24 @@ ...',
          previousFilename: null,
        },
      ],
      parentShas: [MOCK_COMMITS[1]?.sha ?? ''],
    }
    return Promise.resolve(detail)
  }

  // ── Diffs ──────────────────────────────────────────────────────────────────

  compareRefs(_ref: RepoRef, base: string, head: string): Promise<Comparison> {
    const comparison: Comparison = {
      baseRef: base,
      headRef: head,
      aheadBy: 3,
      behindBy: 1,
      status: 'diverged',
      commits: MOCK_COMMITS.slice(0, 3),
      files: MOCK_COMPARISON_FILES,
      additions: 80,
      deletions: 13,
      changedFiles: 3,
    }
    return Promise.resolve(comparison)
  }

  getFileDiff(_ref: RepoRef, base: string, head: string, filePath: string): Promise<FileDiff> {
    const diff: FileDiff = {
      filename: filePath,
      status: 'modified',
      additions: 12,
      deletions: 4,
      hunks: [
        {
          header: '@@ -1,4 +1,12 @@',
          lines: [
            { type: 'context',  content: 'import type { Deployment }', oldLineNumber: 1, newLineNumber: 1 },
            { type: 'deletion', content: '// old comment',            oldLineNumber: 2, newLineNumber: null },
            { type: 'addition', content: '// new comment',            oldLineNumber: null, newLineNumber: 2 },
          ],
        },
      ],
      previousFilename: null,
    }
    void base; void head
    return Promise.resolve(diff)
  }

  // ── Branch file browsing + cross-branch compare ──────────────────────────────
  // A small in-memory repo filesystem, varied per branch so the file browser + the
  // cross-branch diff are both fully exercisable with no backend.

  getBranchTree(_ref: RepoRef, branch: string): Promise<TreeEntry[]> {
    return Promise.resolve(treeFromPaths(Object.keys(mockBranchFiles(branch))))
  }

  readBranchFile(_ref: RepoRef, branch: string, path: string): Promise<string> {
    const body = mockBranchFiles(branch)[path]
    return body === undefined
      ? Promise.reject(new Error(`File not found on ${branch}: ${path}`))
      : Promise.resolve(body)
  }

  compareBranchFiles(_ref: RepoRef, base: string, head: string): Promise<BranchFileChange[]> {
    const baseFiles = mockBranchFiles(base)
    const headFiles = mockBranchFiles(head)
    const paths = [...new Set([...Object.keys(baseFiles), ...Object.keys(headFiles)])].sort()
    const changes: BranchFileChange[] = []
    for (const path of paths) {
      const inBase = path in baseFiles, inHead = path in headFiles
      if (inHead && !inBase) changes.push({ path, status: 'added' })
      else if (inBase && !inHead) changes.push({ path, status: 'removed' })
      else if (baseFiles[path] !== headFiles[path]) changes.push({ path, status: 'modified' })
    }
    return Promise.resolve(changes)
  }

  // ── Pull Requests ──────────────────────────────────────────────────────────

  listPRs(ref: RepoRef, filter?: PRFilter): Promise<PR[]> {
    let result = this.getPRs(ref).map((p) => ({ ...p }))
    if (filter?.state && filter.state !== 'all') {
      result = result.filter((p) => p.state === filter.state)
    }
    if (filter?.branch) {
      result = result.filter((p) => p.head === filter.branch)
    }
    return Promise.resolve(result)
  }

  getPR(ref: RepoRef, prNumber: number): Promise<PRDetail> {
    const pr = this.getPRs(ref).find((p) => p.number === prNumber)
    if (!pr) return Promise.reject(new Error(`PR #${prNumber} not found`))

    const editedBody = this._prBodies.get(`${this.branchKey(ref)}#${prNumber}`)

    if (prNumber === 1) {
      const detail: PRDetail = {
        ...pr,
        body: editedBody ?? 'This PR implements the ARM deployment engine layer logic.',
        commits: MOCK_COMMITS.slice(0, 2),
        files:   MOCK_COMPARISON_FILES,
        reviews: MOCK_REVIEWS_PR1,
        checks:  [],
        additions:    120,
        deletions:     30,
        changedFiles:   3,
      }
      return Promise.resolve(detail)
    }

    // PRs 2 and 3: minimal detail
    const detail: PRDetail = {
      ...pr,
      body: editedBody ?? (prNumber === 2
        ? 'Fixes the retry logic for Layer 03 Product2 type errors.'
        : null),
      commits:     MOCK_COMMITS.slice(0, 1),
      files:       [],
      reviews:     [],
      checks:      [],
      additions:   prNumber === 2 ? 24 : 5,
      deletions:   prNumber === 2 ?  8 : 2,
      changedFiles: prNumber === 2 ?  3 : 1,
    }
    return Promise.resolve(detail)
  }

  createPR(ref: RepoRef, input: CreatePRInput): Promise<PR> {
    const prs = this.getPRs(ref)
    const maxNumber = prs.reduce((max, p) => Math.max(max, p.number), 0)
    const newPR: PR = {
      number: maxNumber + 1,
      title: input.title,
      state: 'open',
      author: { name: 'mock-user', avatarUrl: null },
      head: input.head,
      base: input.base,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      labels: [],
      reviewStatus: 'pending',
      approvalCount: 0,
      ciStatus: 'pending',
      mergeStatus: 'unknown',
      url: `https://dev.azure.com/mock/pullrequest/${maxNumber + 1}`,
    }
    const key = this.branchKey(ref)
    this.prs.set(key, [...prs, newPR])
    return Promise.resolve({ ...newPR })
  }

  updatePR(ref: RepoRef, prNumber: number, patch: { title?: string; body?: string }): Promise<PR> {
    const prs = this.getPRs(ref)
    const idx = prs.findIndex((p) => p.number === prNumber)
    if (idx === -1) return Promise.reject(new Error(`PR #${prNumber} not found`))
    const existing = prs[idx]!
    const updated: PR = {
      ...existing,
      ...(patch.title !== undefined && { title: patch.title }),
      updatedAt: new Date().toISOString(),
    }
    // Track the edited body so getPR() reflects it (PR summary has no body field).
    if (patch.body !== undefined) this._prBodies.set(`${this.branchKey(ref)}#${prNumber}`, patch.body)
    const key = this.branchKey(ref)
    this.prs.set(key, prs.map((p, i) => (i === idx ? updated : p)))
    return Promise.resolve({ ...updated })
  }

  mergePR(ref: RepoRef, prNumber: number, _method: 'merge' | 'squash' | 'rebase'): Promise<void> {
    const prs = this.getPRs(ref)
    const idx = prs.findIndex((p) => p.number === prNumber)
    if (idx === -1) return Promise.reject(new Error(`PR #${prNumber} not found`))
    const updatedPRs = prs.map((p, i) =>
      i === idx ? { ...p, state: 'merged' as const } : p,
    )
    const key = this.branchKey(ref)
    this.prs.set(key, updatedPRs)
    return Promise.resolve()
  }

  getConflicts(_ref: RepoRef, prNumber: number): Promise<MergeConflictReport> {
    // PR #3 is the seeded conflicting PR (mergeStatus: 'conflicts'); anything else has no conflicts.
    if (prNumber !== 3) {
      return Promise.resolve({
        prNumber, baseBranch: 'main', headBranch: 'feature/billing',
        mergeBaseSha: 'mmm0000', baseSha: 'bbb0000', headSha: 'hhh0000', files: [], truncated: false,
      })
    }
    return Promise.resolve({
      prNumber, baseBranch: 'main', headBranch: 'feature/billing',
      mergeBaseSha: 'mmm0000', baseSha: 'bbb0000', headSha: 'hhh0000', truncated: false,
      files: [
        {
          path: 'data/arm/PCM/Product2/58/BILLING-1.json', kind: 'record',
          ancestor:  '{"object":"Product2","globalKey":"BILLING-1","record":{"Name":"Billing","Description":"base"}}',
          target:    '{"object":"Product2","globalKey":"BILLING-1","record":{"Name":"Billing","Description":"target edit"}}',
          incoming:  '{"object":"Product2","globalKey":"BILLING-1","record":{"Name":"Billing","Description":"incoming edit"}}',
        },
        {
          // Non-overlapping edits auto-merge (version → base, run() body → incoming); only `mode`
          // was changed by BOTH → a single per-hunk conflict.
          path: 'force-app/main/default/classes/BillingService.cls', kind: 'text',
          ancestor: "public class BillingService {\n  Integer version = 1;\n  String mode = 'base';\n  Boolean live = false;\n  void run() {}\n}\n",
          target:   "public class BillingService {\n  Integer version = 2;\n  String mode = 'A';\n  Boolean live = false;\n  void run() {}\n}\n",
          incoming: "public class BillingService {\n  Integer version = 1;\n  String mode = 'B';\n  Boolean live = false;\n  void run() { charge(); }\n}\n",
        },
      ],
    })
  }

  resolveConflicts(_ref: RepoRef, _prNumber: number, input: ResolveMergeConflictsInput): Promise<ResolveMergeConflictsResult> {
    return Promise.resolve({ mergeCommitSha: 'mock-merge-sha', resolvedPaths: input.resolutions.map((r) => r.path) })
  }

  /** One of each outcome, so the land review's grouping, decisions and hand-off are all reachable on mocks. */
  /** Unapproved AND authored by Stride — the real situation for a promote PR, so the UI's
   *  "Stride can't approve its own PR" path is what you see on mocks. */
  prApproval(_ref: RepoRef, prNumber: number): Promise<PrApprovalState> {
    return Promise.resolve({
      prNumber, approved: false, approvals: [], changesRequested: [],
      author: 'stride-bot', viewer: 'stride-bot', authoredByStride: true,
      url: `https://github.com/octo/repo/pull/${prNumber}`,
    })
  }

  landReview(_ref: RepoRef, base: string, head: string, _scopePaths?: string[]): Promise<LandReviewReport> {
    const at = (object: string, gk: string, name: string) =>
      JSON.stringify({ object, globalKey: gk, record: { GlobalKey__c: gk, Name: name } })
    return Promise.resolve({
      baseBranch: base, headBranch: head,
      mergeBaseSha: 'mock-merge-base', baseSha: 'mock-base', headSha: 'mock-head',
      records: [
        { path: 'data/arm/PCM/Product2/58/NEW-1.json', kind: 'record', outcome: 'new', object: 'Product2', globalKey: 'gk-new', label: 'Constant Connect' },
        { path: 'data/arm/PCM/Product2/58/OURS-1.json', kind: 'record', outcome: 'ours', object: 'Product2', globalKey: 'gk-ours', label: 'CPE',
          changedFields: ['Description', 'IsActive', 'Type'], targetContent: at('Product2', 'gk-ours', 'CPE') },
        { path: 'data/arm/PCM/ProductClassification/58/SAME-1.json', kind: 'record', outcome: 'same', object: 'ProductClassification', globalKey: 'gk-same', label: 'Broadband' },
        { path: 'data/arm/PCM/Product2/58/BOTH-1.json', kind: 'record', outcome: 'both', object: 'Product2', globalKey: 'gk-both', label: 'Enhanced Security',
          changedFields: ['Description'], targetContent: at('Product2', 'gk-both', 'Enhanced Security') },
      ],
      counts: { new: 1, same: 1, ours: 1, both: 1 },
      truncated: false,
    })
  }

  // ── Releases ───────────────────────────────────────────────────────────────

  listReleases(ref: RepoRef): Promise<Release[]> {
    return Promise.resolve(this.getReleases(ref).map((r) => ({ ...r })))
  }

  getLatestRelease(ref: RepoRef): Promise<Release | null> {
    const latest = this.getReleases(ref).find((r) => !r.isPrerelease && !r.isDraft) ?? null
    return Promise.resolve(latest ? { ...latest } : null)
  }

  createRelease(ref: RepoRef, input: CreateReleaseInput): Promise<Release> {
    const newRelease: Release = {
      id: `rel-${input.tag}`,
      tagName: input.tag,
      name: input.name,
      body: input.body,
      isDraft: input.draft,
      isPrerelease: input.prerelease,
      createdAt:   new Date().toISOString(),
      publishedAt: input.draft ? null : new Date().toISOString(),
      author: { name: 'mock-user', avatarUrl: null },
      commitsSinceLastRelease: null,
    }
    const key = this.branchKey(ref)
    if (!this._releases.has(key)) {
      this._releases.set(key, [...MOCK_RELEASES])
    }
    const existing = this._releases.get(key) ?? []
    this._releases.set(key, [...existing, newRelease])
    return Promise.resolve({ ...newRelease })
  }

  // ── CI / Workflows ─────────────────────────────────────────────────────────

  listWorkflowRuns(_ref: RepoRef, filter?: RunFilter): Promise<WorkflowRun[]> {
    let result = [...MOCK_WORKFLOW_RUNS]
    if (filter?.branch) {
      result = result.filter((r) => r.branch === filter.branch)
    }
    if (filter?.status) {
      result = result.filter((r) => r.status === filter.status)
    }
    return Promise.resolve(result)
  }

  getCommitStatus(_ref: RepoRef, sha: string): Promise<CIStatus> {
    const checks: CICheck[] = [
      {
        name:        'CI / build',
        state:       'success',
        conclusion:  'success',
        url:         'https://dev.azure.com/stratuscarta/arm/_build/results?buildId=1',
        startedAt:   new Date(Date.now() - 200_000).toISOString(),
        completedAt: new Date(Date.now() - 100_000).toISOString(),
      },
      {
        name:        'CI / test',
        state:       'success',
        conclusion:  'success',
        url:         'https://dev.azure.com/stratuscarta/arm/_build/results?buildId=1',
        startedAt:   new Date(Date.now() - 200_000).toISOString(),
        completedAt: new Date(Date.now() -  90_000).toISOString(),
      },
    ]
    return Promise.resolve({ sha, state: 'success', checks })
  }

  scaffoldRepo(_ref: RepoRef): Promise<{ sha: string; message: string }> {
    return Promise.resolve({ sha: 'mock-scaffold-sha', message: 'Scaffolded (mock)' })
  }
}
